import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { type z } from 'zod'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/card/card'
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'

import { resetPasswordMutation } from '@/lib/queries/business/v1/auth/password/reset/mutations'

import { IndependentPageTopBar } from '../ui/individual-page-top-bar/individual-page-top-bar'
import { formSchema } from './constants/zod'

export const ForgotPasswordPage = () => {
  const { isSettingInitialGooglePassword } = useSearch({
    from: '/forgot-password',
  })

  const mutation = useMutation(resetPasswordMutation)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '' },
  })

  const handleSubmit = useCallback((values: z.infer<typeof formSchema>) => {
    mutation.mutate(values, {
      onSuccess: () => {
        toast.success('Password reset instructions sent. Please check your email.')
      },
      onError: () => {
        toast.error('Unable to send the link. Please try again later.')
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <IndependentPageTopBar />
      <div className="mt-[150px] flex flex-col space-y-6">
        <Card className="mx-auto w-[407px]">
          <CardHeader>
            <CardTitle className="leading-8">
              {isSettingInitialGooglePassword ? 'Reset Password' : 'Forgot password?'}
            </CardTitle>
            <CardDescription>
              Enter your email address and we will send you instructions to reset your password.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <div className="grid w-full items-center gap-4">
                <form className="grid w-full items-center gap-6" onSubmit={form.handleSubmit(handleSubmit)}>
                  {/* Input: email */}
                  <FormField
                    control={form.control}
                    rules={{ required: true }}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            autoFocus
                            className="placeholder:text-faint-foreground"
                            placeholder="email@example.com"
                            disabled={mutation.isPending || mutation.isSuccess}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Submit */}
                  <Button
                    className="mb-2 w-full"
                    type="submit"
                    size="sm"
                    variant="default"
                    disabled={mutation.isPending || mutation.isSuccess}
                  >
                    {mutation.isSuccess ? 'Sent' : 'Continue'}
                  </Button>
                </form>
              </div>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
